"use client"

import { FormSection, FormShell, FormSkeleton, StickyActionBar } from "@/(admin)/components/form"
import { StatusPublish, VisibilityPublish } from "@/(admin)/modules/post/pages/StatusPublish"
import AccessDenied from "@/components/custom-access-denied"
import { useCategory } from "@/hook/useCategory"
import { useGuardedForm } from "@/hook/useGuardedForm"
import { t } from "@/languages/i18n"
import { MediaData } from "@/lib/config/types"
import { checkSlugCategory } from "@/services/category"
import { useDomainStore } from "@/stores/use-domain-store"
import { CategoryDataWithLocale } from "@/types/types"
import { normalizeFormData } from "@/utils/normalize-form-data"
import { generateAndValidateSlug } from "@/utils/slug-utils"
import { SaveOutlined, SendOutlined } from "@ant-design/icons"
import { Button, Form, Input } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import React, { useCallback, useEffect, useState } from "react"
import { CATEGORIES } from "../../../../constants/admin-paths"
import FeaturedImage from "../../post/pages/FeaturedImage"
import CategoryFormFields from "../components/CategoryFormFields"
import { CategoryLanguagePublish } from "../components/CategoryLanguagePublish"

const isValidJson = (str: string): boolean => {
  try {
    JSON.parse(str)
    return true
  } catch {
    return false
  }
}

interface FormUpsertCategoryProps {
  onCategoryAdded?: () => void
}

const FormUpsertCategory: React.FC<FormUpsertCategoryProps> = ({ onCategoryAdded }) => {
  const [form] = Form.useForm()
  const searchParams = useSearchParams()
  const id = searchParams.get("id")
  const router = useRouter()
  // tamdamedia.eu chỉ có tiếng Việt → khoá ngôn ngữ ở VI.
  const isTamdaMedia = useDomainStore(state => state.selectedDomain?.attributes?.domain === "tamdamedia.eu")
  const [isEditableSlug, setIsEditableSlug] = useState(!id)
  const isEditMode = Boolean(id)

  const [status, setStatus] = useState("Draft")
  const [language, setLanguage] = useState("vi")
  const [publishDate, setPublishDate] = useState<string | null>(null)
  const [bannerMedia, setBannerMedia] = useState<MediaData | null>(null)
  const [categoryName, setCategoryName] = useState<string>("")

  const { loading, fetchCategoryById, createCategory, updateCategory, hasPermission } = useCategory()

  const canAccess = isEditMode ? hasPermission("update") : hasPermission("create")
  const [isFormReady, setIsFormReady] = useState(false)
  const getNormalizedSnapshot = useCallback(() => {
    return {
      ...normalizeFormData(form.getFieldsValue(true)),
      status,
      language,
      publishDate,
      bannerId: bannerMedia?.id ?? null,
    }
  }, [form, status, language, publishDate, bannerMedia])

  const { isChange, checkChange, commitSnapshot } = useGuardedForm({
    form,
    getSnapshot: getNormalizedSnapshot,
  })

  useEffect(() => {
    checkChange()
  }, [status, language, publishDate, bannerMedia, checkChange])

  useEffect(() => {
    if (!isEditMode) {
      setIsFormReady(true)
    }
  }, [isEditMode])

  const loadCategory = async () => {
    const data = await fetchCategoryById(Number(id))
    const categoryData = data as CategoryDataWithLocale

    if (categoryData && categoryData.attributes) {
      const seo = categoryData.attributes.seo
      form.setFieldsValue({
        name: categoryData.attributes.name,
        slug: categoryData.attributes.slug,
        description: categoryData.attributes.description,
        domain: categoryData.attributes.domain || "",
        type: categoryData.attributes.type || "",
        playlistUrl: categoryData.attributes.playlistUrl || "",
        metaTitle: seo?.metaTitle || "",
        metaDescription: seo?.metaDescription || "",
        keywords: seo?.keywords || "",
        metaRobots: seo?.metaRobots || "",
        metaViewport: seo?.metaViewport || "",
        canonicalURL: seo?.canonicalURL || "",
        structuredData: seo?.structuredData ? JSON.stringify(seo.structuredData) : "",
      })

      setCategoryName(categoryData.attributes.name || "")

      const bannerData = (categoryData.attributes as any).banner?.data
      setBannerMedia(bannerData ? ({ id: bannerData.id, url: bannerData.attributes?.url } as MediaData) : null)

      const publishedAt = categoryData.attributes.publishedAt
      if (publishedAt) {
        setStatus("Published")
        setPublishDate(publishedAt)
      } else {
        setStatus("Draft")
        setPublishDate(null)
      }

      if (categoryData.attributes.locale) {
        setLanguage(categoryData.attributes.locale)
      }
      setIsFormReady(true)
    }
  }

  useEffect(() => {
    if (isFormReady) {
      const animationId = requestAnimationFrame(() => {
        commitSnapshot()
      })
      return () => cancelAnimationFrame(animationId)
    }
  }, [isFormReady, commitSnapshot])

  const handleNameBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    if (isEditMode) return
    const nameValue = e.target.value.trim()
    if (!nameValue) {
      form.setFields([{ name: "slug", errors: [] }])
      return
    }
    const { slug, isUnique } = await generateAndValidateSlug(nameValue, checkSlugCategory)
    form.setFieldsValue({ slug })
    if (!isUnique) {
      form.setFields([{ name: "slug", errors: [t("Đã tồn tại. Vui lòng chọn một đường dẫn khác")] }])
    } else {
      form.setFields([{ name: "slug", errors: [] }])
    }
    setTimeout(() => {
      checkChange()
    }, 0)
  }

  const handleSubmit = async () => {
    const values = form.getFieldsValue()

    let currentPublishedAt = null
    if (status === "Published") {
      currentPublishedAt = publishDate ? publishDate : new Date().toISOString()
    }

    // structuredData đã được SeoForm validate là JSON hợp lệ (hoặc rỗng) trước
    // khi onFinish chạy, nên parse an toàn; rỗng → null để Strapi bỏ trống.
    const { metaTitle, metaDescription, keywords, metaRobots, metaViewport, canonicalURL, structuredData, ...rest } =
      values

    const payload = {
      ...rest,
      domain: values.domain || null,
      banner: bannerMedia?.id ?? null,
      publishedAt: currentPublishedAt,
      locale: language,
      seo: {
        metaTitle: metaTitle || null,
        metaDescription: metaDescription || null,
        keywords: keywords || null,
        metaRobots: metaRobots || null,
        metaViewport: metaViewport || null,
        canonicalURL: canonicalURL || null,
        structuredData: structuredData ? JSON.parse(structuredData) : null,
      },
    }

    const params = new URLSearchParams(searchParams.toString())
    const queryString = params.toString()

    if (isEditMode && id) {
      const success = await updateCategory(id, payload)
      if (success) {
        commitSnapshot()
        if (onCategoryAdded) {
          onCategoryAdded()
        }
        router.push(`/admin/${CATEGORIES.LIST.PATH}${queryString ? `?${queryString}` : ""}`)
      }
    } else {
      const success = await createCategory(payload)
      if (success) {
        commitSnapshot()

        if (onCategoryAdded) {
          onCategoryAdded()
        }
        form.resetFields()
        router.push(`/admin/${CATEGORIES.LIST.PATH}${queryString ? `?${queryString}` : ""}`)
      }
    }
  }

  useEffect(() => {
    if (canAccess && isEditMode && id) {
      loadCategory()
    }
  }, [id, canAccess, isEditMode])

  if (!canAccess) {
    return <AccessDenied />
  }

  if (isEditMode && !isFormReady) {
    return <FormSkeleton mainSections={2} sidebarCards={2} />
  }

  const primaryLabel = status === "Draft" ? t("Lưu bản nháp") : isEditMode ? t("Cập nhật") : t("Xuất bản")

  const crumbs = [
    { title: t("Danh mục"), href: `/admin${CATEGORIES.LIST.PATH}` },
    { title: isEditMode ? t("Chỉnh sửa") : t("Tạo mới") },
  ]

  const main = (
    <Form labelAlign="left" form={form} onFinish={handleSubmit} layout="vertical" onValuesChange={checkChange}>
      <FormSection title={t("Cơ bản")} collapsible={false}>
        <CategoryFormFields
          form={form}
          isEditMode={isEditMode}
          isEditableSlug={isEditableSlug}
          onSlugToggle={() => setIsEditableSlug(!isEditableSlug)}
          onNameBlur={handleNameBlur}
        />
      </FormSection>

      <FormSection title="SEO" defaultOpen={false} description={t("Tuỳ biến khi cần")}>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="metaTitle" label={t("Meta title")}>
            <Input placeholder={t("Mặc định lấy từ tên danh mục")} />
          </Form.Item>

          <Form.Item name="keywords" label={t("Từ khoá")}>
            <Input placeholder={t("Cách nhau bằng dấu phẩy")} />
          </Form.Item>

          <Form.Item name="metaDescription" label={t("Meta description")} className="col-span-2">
            <Input.TextArea rows={3} placeholder={t("Mặc định lấy từ mô tả")} showCount maxLength={160} />
          </Form.Item>

          <Form.Item
            name="structuredData"
            label={t("Structured data (JSON-LD)")}
            className="col-span-2"
            rules={[
              {
                validator: (_, value) => {
                  if (value && !isValidJson(value)) {
                    return Promise.reject(new Error(t("JSON không hợp lệ")))
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            <Input.TextArea rows={4} placeholder='{"@context": "https://schema.org", ...}' />
          </Form.Item>

          <Form.Item name="metaRobots" label={t("Meta robots")}>
            <Input placeholder="index, follow" />
          </Form.Item>

          <Form.Item name="metaViewport" label={t("Meta viewport")}>
            <Input placeholder="width=device-width" />
          </Form.Item>

          <Form.Item name="canonicalURL" label={t("Canonical URL")} className="col-span-2">
            <Input placeholder="https://..." />
          </Form.Item>
        </div>
      </FormSection>
    </Form>
  )

  const sidebar = (
    <>
      <FormSection title={t("Xuất bản")} collapsible={false} compact>
        <div className="flex flex-col gap-3">
          <StatusPublish setStatus={setStatus} status={status} />
          <CategoryLanguagePublish language={language} setLanguages={setLanguage} disabled={isEditMode || isTamdaMedia} />
          <VisibilityPublish status={status} />
        </div>
      </FormSection>

      <FormSection title={t("Banner")} compact description={t("Áp dụng cho cụm Tamda Group")}>
        <FeaturedImage selectedMedia={bannerMedia} setSelectedMedia={setBannerMedia} />
      </FormSection>
    </>
  )

  const actionBar = (
    <StickyActionBar
      right={
        <Button
          type="primary"
          icon={status === "Draft" ? <SaveOutlined /> : <SendOutlined />}
          onClick={() => form.submit()}
          loading={loading}
          disabled={isEditMode && !isChange}
        >
          {primaryLabel}
        </Button>
      }
    />
  )

  return (
    <FormShell
      title={isEditMode ? t("Chỉnh sửa Danh Mục") : t("Thêm Danh Mục")}
      subtitle={isEditMode && categoryName ? categoryName : undefined}
      crumbs={crumbs}
      main={main}
      sidebar={sidebar}
      actionBar={actionBar}
    />
  )
}

export default FormUpsertCategory
